import React, { useRef } from "react";
import Footer from "components/footer/FooterAuthDefault";
import { Routes, Route, Navigate } from "react-router-dom";
import routes from "routes.js";
import FixedPlugin from "components/fixedPlugin/FixedPlugin";
import { Canvas, useFrame } from "@react-three/fiber";
import { Text, Environment } from "@react-three/drei";

/* ─── Orbiting shine light ─── */
function ShineLight() {
  const light = useRef();
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (light.current) {
      light.current.position.set(
        Math.sin(t * 0.8) * 6,
        Math.cos(t * 0.5) * 4,
        4
      );
    }
  });
  return <pointLight ref={light} color="#ffffff" intensity={7} distance={18} />;
}

/* ─── 3-D Coin ─── */
function Coin3D() {
  const tiltRef = useRef();
  const spinRef = useRef();
  const floatRef = useRef();

  useFrame((state, delta) => {
    const t = state.clock.getElapsedTime();

    // coin spin
    if (spinRef.current) {
      spinRef.current.rotation.z -= delta * 0.45;
    }

    // floating animation
    if (floatRef.current) {
      floatRef.current.position.y = Math.sin(t * 0.85) * 0.22;
    }

    // mouse tilt
    if (tiltRef.current) {
      const tx = state.pointer.y * 0.38;
      const ty = -state.pointer.x * 0.38;

      tiltRef.current.rotation.x += (tx - tiltRef.current.rotation.x) * 0.06;
      tiltRef.current.rotation.y += (ty - tiltRef.current.rotation.y) * 0.06;
    }
  });

  const COIN_R = 2.7;
  const COIN_H = 0.32;
  const SEGMENTS = 80;

  return (
    <group ref={tiltRef}>
      <group ref={floatRef}>
        <group rotation={[Math.PI / 2, 0, 0]}>
          <group ref={spinRef}>

            {/* coin body – glass transparent */}
            <mesh>
              <cylinderGeometry args={[COIN_R, COIN_R, COIN_H, SEGMENTS]} />
              <meshPhysicalMaterial
                color="#ffffff"
                transmission={0.92}
                transparent
                opacity={0.18}
                metalness={0.0}
                roughness={0.05}
                thickness={0.5}
                ior={1.45}
                reflectivity={0.6}
              />
            </mesh>

            {/* inner disc – subtle white frost */}
            <mesh position={[0, 0.012, 0]}>
              <cylinderGeometry args={[COIN_R - 0.22, COIN_R - 0.22, COIN_H + 0.02, SEGMENTS]} />
              <meshPhysicalMaterial
                color="#ffffff"
                transmission={0.85}
                transparent
                opacity={0.12}
                metalness={0.0}
                roughness={0.08}
                thickness={0.3}
                ior={1.4}
              />
            </mesh>

            {/* orange border ring front */}
            <mesh position={[0, COIN_H / 2 + 0.005, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[COIN_R - 0.5, 0.07, 8, SEGMENTS]} />
              <meshStandardMaterial color="#FF7A00" metalness={0.95} roughness={0.05} emissive="#FF4400" emissiveIntensity={0.4} />
            </mesh>

            {/* orange border ring back */}
            {/* <mesh position={[0, -(COIN_H / 2 + 0.005), 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[COIN_R - 0.5, 0.07, 8, SEGMENTS]} />
              <meshStandardMaterial color="#FF7A00" metalness={0.95} roughness={0.05} emissive="#FF4400" emissiveIntensity={0.4} />
            </mesh> */}

            {/* white outer rim ring front */}
            <mesh position={[0, COIN_H / 2 + 0.003, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[COIN_R - 0.08, 0.045, 8, SEGMENTS]} />
              <meshStandardMaterial color="#ffffff" metalness={0.3} roughness={0.1} emissive="#ffffff" emissiveIntensity={0.3} />
            </mesh>

            {/* white outer rim ring back */}
            {/* <mesh position={[0, -(COIN_H / 2 + 0.003), 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[COIN_R - 0.08, 0.045, 8, SEGMENTS]} />
              <meshStandardMaterial color="#ffffff" metalness={0.3} roughness={0.1} emissive="#ffffff" emissiveIntensity={0.3} />
            </mesh> */}

            {/* coin edge ridges – alternating orange/white */}
            {/* {Array.from({ length: 52 }).map((_, i) => {
              const angle = (i / 52) * Math.PI * 2;
              const isOrange = i % 2 === 0;
              return (
                <mesh
                  key={i}
                  position={[
                    (COIN_R - 0.03) * Math.cos(angle),
                    0,
                    (COIN_R - 0.03) * Math.sin(angle),
                  ]}
                  rotation={[0, -angle, 0]}
                >
                  <boxGeometry args={[0.07, COIN_H + 0.01, 0.13]} />
                  <meshStandardMaterial
                    color={isOrange ? "#FF7A00" : "#ffffff"}
                    metalness={0.9}
                    roughness={0.1}
                    emissive={isOrange ? "#FF4400" : "#ffffff"}
                    emissiveIntensity={0.25}
                  />
                </mesh>
              );
            })} */}

            {/* FRONT T */}
            <Text
              position={[0, COIN_H / 2 + 0.012, 0]}
              rotation={[-Math.PI / 2, 0, 0]}
              fontSize={2.8}
              color="#FF7A00"
              anchorX="center"
              anchorY="middle"
              outlineWidth={0.055}
              outlineColor="#ffffff"
              depthOffset={-10}
              renderOrder={999}
              material-depthWrite={false}
            >
              T
            </Text>

            {/* BACK T */}
            <Text
              position={[0, -(COIN_H / 2 + 0.012), 0]}
              rotation={[Math.PI / 2, 0, Math.PI]}
              fontSize={2.8}
              color="#FF7A00"
              anchorX="center"
              anchorY="middle"
              outlineWidth={0.055}
              outlineColor="#ffffff"
              depthOffset={-10}
              renderOrder={999}
              material-depthWrite={false}
            >
              T
            </Text>

          </group>
        </group>

        {/* shadow */}
        <mesh position={[0, -3.4, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[2.2, 0.55, 1]}>
          <circleGeometry args={[1, 40]} />
          <meshBasicMaterial color="#FF4400" transparent opacity={0.18} />
        </mesh>

      </group>
    </group>
  );
}

/* ─── Auth Layout ─── */
export default function Auth() {
  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/auth") {
        return (
          <Route path={`/${prop.path}`} element={prop.component} key={key} />
        );
      }
      return null;
    });
  };

  document.documentElement.dir = "ltr";

  return (
    <div className="relative min-h-screen w-full !bg-white dark:!bg-navy-900">
      <FixedPlugin />
      <main className="flex min-h-screen">

        {/* ── Left: form (full width mobile, 50% on md+) ── */}
        <div className="flex w-full flex-col items-center justify-center md:w-1/2">
          <div className="flex w-full flex-col items-center justify-center px-6 py-10 sm:px-10 md:px-12 lg:px-16">
            <Routes>
              {getRoutes(routes)}
              <Route path="/" element={<Navigate to="/auth/sign-in" replace />} />
            </Routes>
          </div>
          <Footer />
        </div>

        {/* ── Right: 3-D coin panel (hidden on mobile, 50% on md+) ── */}
        <div className="hidden md:block md:w-1/2">
          <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF9800] via-[#FF7A00] to-[#FF5500] lg:rounded-bl-[120px] xl:rounded-bl-[150px]">

            {/* Background glow blobs */}
            <div className="absolute top-1/4 left-1/2 h-72 w-72 rounded-full bg-white/10 blur-[90px]" />
            <div className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-orange-300/20 blur-[100px]" />

            {/* 3-D Coin Canvas */}
            <div className="relative z-10 w-full" style={{ height: 420 }}>
              <Canvas
                camera={{ position: [0, 0, 9], fov: 42 }}
                dpr={[1, 2]}
                style={{ width: "100%", height: "100%" }}
              >
                <ambientLight intensity={2.5} color="#ffffff" />
                <directionalLight position={[4, 6, 8]} intensity={2.5} color="#ffffff" />
                <directionalLight position={[-6, 2, 5]} intensity={1.2} color="#ffe0b2" />
                <pointLight position={[0, -5, -4]} color="#ff6d00" intensity={2} />
                <ShineLight />
                <Environment preset="studio" />
                <Coin3D />
              </Canvas>
            </div>

            {/* Branding text below coin */}
            <div className="relative z-10 mt-2 flex flex-col items-center gap-2 px-6 text-center">
              <h1
                className="text-[52px] font-black leading-none tracking-tight"
                style={{ color: "#FF7A00", fontFamily: "'Impact', 'Arial Black', sans-serif" }}
              >
                TINKA
              </h1>
              <p className="text-sm font-semibold uppercase tracking-widest text-white/80">
                Your trading reputation
              </p>
              <div className="mt-1 flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-1.5 backdrop-blur-sm">
                <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-bold tracking-wide text-white">Exchange-verified</span>
              </div>
            </div>

          </div>
        </div>

      </main>
    </div>
  );
}
